import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccessVisibility, SystemEntity } from '@wdx/clmi/clmi-swagger-gen';
import { ModalManagementService } from '@wdx/clmi/ui';
import { PartyToAvatarPipe } from '@wdx/clmi/utils/pipes';
import { EntityRouteService } from '@wdx/clmi/utils/services';
import { CrudStatus, KeyValueObject } from '@wdx/shared/utils';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, map, switchMap } from 'rxjs/operators';
import { Table } from '../../../models/table.model';
import { EntityQueryParamPipe } from '../../../pipes/entity-query-param.pipe';
import { RandomStringPipe } from '../../../pipes/random-string.pipe';
import { AccessVisibilityFacade } from './shared/services/access-visibility.facade';

@Component({
    selector: 'clmi-access-visibility',
    styleUrls: ['./access-visibility.component.scss'],
    templateUrl: './access-visibility.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AccessVisibilityComponent implements OnInit {
    modalId: string;

    accessVisibility$: Observable<AccessVisibility[]>;
    status$: Observable<CrudStatus>;

    accessVisibilityTable$: Observable<Table>;

    searchResultsTable: Table;

    private entityType$ = new BehaviorSubject<SystemEntity>(null);
    private entityId$ = new BehaviorSubject<string>(null);

    @ViewChild('nameCell', { read: TemplateRef }) nameCell: TemplateRef<any>;
    @ViewChild('reasonCell', { read: TemplateRef })
    reasonCell: TemplateRef<any>;

    public readonly CrudStatus = CrudStatus;

    private entityRouteService = inject(EntityRouteService);
    private accessVisibilityFacade = inject(AccessVisibilityFacade);
    private modalManagementService = inject(ModalManagementService);
    private destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.modalId = new RandomStringPipe().transform();

        this.accessVisibility$ = combineLatest([
            this.entityType$,
            this.entityId$,
        ]).pipe(
            filter(
                ([entityType, entityId]) =>
                    Boolean(entityType) && Boolean(entityId),
            ),
            debounceTime(100),
            switchMap(([entityType, entityId]) =>
                this.accessVisibilityFacade.getAccessVisibility$(
                    entityType,
                    entityId,
                ),
            ),
            takeUntilDestroyed(this.destroyRef),
        );
        this.status$ = combineLatest([this.entityType$, this.entityId$]).pipe(
            filter(
                ([entityType, entityId]) =>
                    Boolean(entityType) && Boolean(entityId),
            ),
            debounceTime(100),
            switchMap(([entityType, entityId]) =>
                this.accessVisibilityFacade.getAccessVisibilityStatus$(
                    entityType,
                    entityId,
                ),
            ),
            takeUntilDestroyed(this.destroyRef),
        );

        this.accessVisibilityTable$ = this.accessVisibility$.pipe(
            map((accessVisibility) => this.createTable(accessVisibility)),
        );
    }

    open(entityType: SystemEntity, entityId: string): void {
        this.entityType$.next(entityType);
        this.entityId$.next(entityId);
        this.modalManagementService.openModalWithId(this.modalId);
    }

    close(): void {
        this.modalManagementService.closeActiveModal();
    }

    onSearch(results: AccessVisibility[]): void {
        this.searchResultsTable = results && this.createTable(results);
    }

    private createTable(accessVisibility: AccessVisibility[]): Table {
        return (
            accessVisibility && {
                headers: [
                    {
                        value: 'Name',
                    },
                    {
                        value: 'Reason',
                    },
                ],
                rows: accessVisibility.map((row) => ({
                    values: [
                        {
                            templateRef: this.nameCell,
                            data: {
                                avatars: [
                                    new PartyToAvatarPipe().transform(
                                        row.accessor,
                                    ),
                                ],
                                name: row.accessor.name,
                                routerLink:
                                    this.entityRouteService.routeForEntity(
                                        SystemEntity[row.accessor?.partyType],
                                        row.accessor?.id,
                                    ),
                                queryParams:
                                    new EntityQueryParamPipe().transform(
                                        SystemEntity[row.accessor?.partyType],
                                    ),
                            },
                        },
                        {
                            templateRef: this.reasonCell,
                            data: {
                                reasons: [...row.reasons]
                                    .sort((a, b) => {
                                        const reasonA = a.reason.toUpperCase();
                                        const reasonB = b.reason.toUpperCase();
                                        return reasonA < reasonB
                                            ? -1
                                            : reasonA > reasonB
                                              ? 1
                                              : 0;
                                    })
                                    .reduce((accumulator, reason) => {
                                        const key = `${reason.reason}`;
                                        return (accumulator = {
                                            ...accumulator,
                                            [key]: [
                                                ...(accumulator[key] || []),
                                                reason.displayMessage.value,
                                            ],
                                        });
                                    }, {} as KeyValueObject),
                            },
                        },
                    ],
                })),
            }
        );
    }
}
